import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store/index";
import api, { addRefreshSubscriber, isRefreshing } from "@/api";

type ProfileLike = { id?: string | number } | null;

const hasCapability = (profile: any, capability: string) =>
  !capability || Boolean(profile?.authorization?.capabilities?.[capability]);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "forum-page",
    // Optimalizáció: route-level code splitting, hogy az első bundle kisebb legyen.
    component: () => import("@/views/ForumView.vue"),
    meta: { title: "VSK Blog" },
  },
  {
    path: "/blog/:id",
    name: "blog-post-page",
    // Optimalizáció: csak akkor töltjük le, amikor tényleg erre a route-ra lép a user.
    component: () => import("@/views/BlogDetailView.vue"),
    props: true,
    meta: { title: "VSK Blog bejegyzés" },
  },
  {
    path: "/login",
    name: "login-page",
    component: () => import("@/views/LoginView.vue"),
    meta: { title: "VSK Bejelentkezés" },
  },
  {
    path: "/register",
    name: "register-page",
    component: () => import("@/views/RegistrationView.vue"),
    meta: { title: "VSK Regisztráció" },
  },
  {
    path: "/users",
    name: "users-page",
    component: () => import("@/views/adminPages/UsersView.vue"),
    meta: { title: "VSK Felhasználók", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);

        // Ha nincs Permission vagy nincs power vagy power kisebb mint 100
        if (!profileData.Permission || permissionPower < 100) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs jogosultságod a statisztikák megtekintéséhez.",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error: any) {
        store.dispatch("setSnackbar", {
          title: "Rendszer üzenet",
          text:
            error?.response?.data?.responseData?.responseMessage ||
            "Nincs jogosultságod a statisztikák megtekintéséhez.",
          color: "error",
        });
        return next({ name: "forum-page" });
      }
    },
  },
  {
    path: "/developer",
    name: "developer-tools-page",
    component: () => import("@/views/adminPages/DeveloperToolsView.vue"),
    meta: { title: "Fejlesztői Eszközök", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/developer/frontend-studio",
    name: "developer-frontend-studio-page",
    component: () =>
      import("@/views/adminPages/DeveloperFrontendStudioView.vue"),
    meta: { title: "Frontend Stúdió", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/developer/errors",
    name: "developer-errors-page",
    component: () => import("@/views/adminPages/DeveloperErrorsView.vue"),
    meta: { title: "Hibaközpont", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/developer/emails",
    name: "developer-emails-page",
    component: () => import("@/views/adminPages/DeveloperEmailLogsView.vue"),
    meta: { title: "Email Napló", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/developer/audit",
    name: "developer-audit-page",
    component: () => import("@/views/adminPages/DeveloperAuditLogsView.vue"),
    meta: { title: "Audit Napló", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/developer/statistics-settings",
    name: "developer-statistics-settings-page",
    component: () =>
      import("@/views/adminPages/DeveloperStatisticsSettingsView.vue"),
    meta: { title: "Statisztika beállítások", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const profileData = await store.dispatch("getProfileData");
        const permissionPower = Number(profileData?.Permission?.power || 0);
        if (!profileData.Permission || permissionPower < 500) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error) {
        store.dispatch("setSnackbar", {
          title: "Hiba",
          text: "Hiba történt a jogosultság ellenőrzése során.",
          color: "error",
        });
        console.error(error);
        return next({ name: "login-page" });
      }
    },
  },
  {
    path: "/statistics",
    name: "statistics-page",
    component: () => import("@/views/authenticatedPages/StatisticsView.vue"),
    meta: { title: "Statisztikák", requiresLogin: true },
    beforeEnter: async (to: any, from: any, next: any) => {
      try {
        const res = await api.get("/profile/statistics-access");
        const canView = Boolean(res?.data?.responseData?.canView);
        if (!canView) {
          store.dispatch("setSnackbar", {
            title: "Rendszer üzenet",
            text: "Nincs jogosultságod a statisztikák megtekintéséhez.",
            color: "error",
          });
          return next({ name: "forum-page" });
        }
        return next();
      } catch (error: any) {
        store.dispatch("setSnackbar", {
          title: "Rendszer üzenet",
          text:
            error?.response?.data?.responseData?.responseMessage ||
            "Nincs jogosultságod a statisztikák megtekintéséhez.",
          color: "error",
        });
        return next({ name: "forum-page" });
      }
    },
  },
  {
    path: "/profile",
    name: "profile-page",
    component: () => import("@/views/authenticatedPages/ProfileView.vue"),
    meta: { title: "Profilom", requiresLogin: true },
  },
  {
    path: "/games",
    name: "games-page",
    component: () => import("@/views/authenticatedPages/GamesView.vue"),
    meta: { title: "VSK Játékok", requiresLogin: true },
  },
  {
    path: "/games/:id",
    name: "games-page-legacy-detail",
    redirect: (to: any) => ({
      name: "games-page",
      query: { eventId: to.params.id },
    }),
    meta: { title: "VSK Játékok", requiresLogin: true },
  },
  {
    path: "/maps",
    name: "maps-page",
    component: () => import("@/views/authenticatedPages/MapsView.vue"),
    meta: { title: "Játék Pályák", requiresLogin: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("@/views/forgotPasswordView.vue"),
    meta: { title: "Elfelejtett jelszó" },
  },
  {
    path: "/reset-password/:identifier",
    name: "reset-password",
    component: () => import("@/views/resetPasswordView.vue"),
    meta: { title: "Jelszó visszaállítás" },
  },
  {
    path: "/account/activate/:userid",
    name: "activate-account-page",
    component: () => import("@/views/ActivateAccountView.vue"),
    meta: { title: "Profil megerősítése" },
  },
  {
    path: "/:NotFound",
    name: "NotFound",
    component: () => import("@/views/NotFound.vue"),
    meta: { title: "404 Hiba" },
  },
  {
    path: "/blog/:id/edit",
    name: "BlogEdit",
    component: () => import("@/views/adminPages/BlogEditView.vue"),
    meta: { title: "Blog szerkesztése", requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to: any, from: any, next: any) => {
  document.title = to.meta.title as string;

  if (to.meta.requiresLogin) {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      store.dispatch("setSnackbar", {
        color: "error",
        text: "Előbb jelentkezz be az oldal eléréséhez",
        icon: "mdi-alert",
      });
      return next({ name: "login-page" });
    }

    try {
      if (isRefreshing) {
        await new Promise<void>((resolve) => {
          addRefreshSubscriber(() => resolve());
        });
      }

      // Optimalizáció: route váltáskor rövid cache-időn belül ne kérjük le újra a profilt.
      await store.dispatch("loadUserFromToken", { force: false });

      const profile = store.state.profile as ProfileLike;
      if (!profile?.id) {
        throw new Error("No user profile found");
      }

      const requiredCapability = String(to.meta.requiredCapability || "");
      if (!hasCapability(profile, requiredCapability)) {
        store.dispatch("setSnackbar", {
          title: "Rendszer üzenet",
          text: "Nincs megfelelő jogosultságod az oldal megtekintéséhez!",
          color: "error",
        });
        return next({ name: "forum-page" });
      }

      return next();
    } catch (err) {
      console.error("Auth error:", err);

      store.dispatch("setSnackbar", {
        color: "error",
        text: "A bejelentkezés lejárt vagy érvénytelen. Kérlek jelentkezz be újra.",
        icon: "mdi-alert",
      });

      return next({ name: "login-page" });
    }
  }

  return next();
});

router.afterEach((to: any, from: any) => {
  const token = localStorage.getItem("accessToken");
  if (!token) return;
  if (!to?.fullPath) return;

  api
    .post("/profile/page-visit", {
      routeName: String(to?.name || ""),
      path: String(to.fullPath || ""),
      referrerPath: String(from?.fullPath || ""),
    })
    .catch((error: unknown) => {
      void error;
    });
});

export default router;
